import React, { useState, useEffect } from 'react';

/**
 * ExportModal – column-picker + TSV/Excel download.
 *
 * Props:
 *   open          boolean  – whether modal is visible
 *   onClose       fn       – called when modal is closed
 *   columnDefs    array    – AG-Grid column defs (must have .field and .headerName)
 *   gridApi       object   – live AG-Grid api instance
 *   selectedOnly  boolean  – when true, export only selected rows
 *   onNotify      fn       – (message, type?) callback to surface user-facing feedback
 */
export default function ExportModal({ open, onClose, columnDefs, gridApi, selectedOnly, onNotify }) {
  // Columns eligible for export: must have a field and not be the checkbox column
  const exportableCols = (columnDefs || []).filter(
    c => c.field && c.field !== '__checkbox__'
  );

  const [checkedFields, setCheckedFields] = useState(() =>
    new Set(exportableCols.filter(c => !c.hide).map(c => c.field))
  );

  // Sync when columnDefs change (e.g. tissue columns added/removed)
  useEffect(() => {
    setCheckedFields(new Set(exportableCols.filter(c => !c.hide).map(c => c.field)));
  }, [columnDefs]); // eslint-disable-line

  if (!open) return null;

  const toggleCol = field => {
    setCheckedFields(prev => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });
  };

  const selectAll = () =>
    setCheckedFields(new Set(exportableCols.map(c => c.field)));
  const deselectAll = () => setCheckedFields(new Set());

  /** Collect rows and selected columns, return { headers, rows } */
  const buildData = () => {
    const selectedCols = exportableCols.filter(c => checkedFields.has(c.field));
    const headers = selectedCols.map(c => c.headerName || c.field);

    const selectedIds = selectedOnly && gridApi
      ? new Set(
          gridApi
            .getSelectedNodes()
            .map(n => n.data?.uniprotId)
            .filter(Boolean)
        )
      : null;

    const rows = [];
    if (gridApi) {
      gridApi.forEachNodeAfterFilterAndSort(node => {
        const row = node.data;
        if (!row) return;
        if (selectedIds && !selectedIds.has(row.uniprotId)) return;

        const cells = selectedCols.map(col => {
          let value = row[col.field];
          if (col.field === 'grade' && value != null) {
            // Export numeric grade value, not stars
            value = typeof value === 'number' ? parseFloat(value.toFixed(2)) : value;
          }
          if (value == null) return '';
          if (Array.isArray(value)) return value.join(', ');
          return value;
        });
        rows.push(cells);
      });
    }
    return { headers, rows, selectedCols };
  };

  const handleTSV = () => {
    const { headers, rows } = buildData();
    if (rows.length === 0) {
      onNotify?.('Nothing to export — the protein list is empty.', 'error');
      return;
    }
    const lines = [headers.join('\t'), ...rows.map(r => r.map(v => String(v).replace(/\t/g, ' ').replace(/\r?\n/g, ' ')).join('\t'))];
    const blob = new Blob([lines.join('\n')], { type: 'text/tab-separated-values' });
    triggerDownload(blob, 'proteins_export.tsv');
    const noun = rows.length === 1 ? 'protein' : 'proteins';
    onNotify?.(
      selectedOnly
        ? `Exported ${rows.length} selected ${noun} as proteins_export.tsv.`
        : `Exported all ${rows.length} ${noun} currently shown as proteins_export.tsv.`
    );
    onClose?.();
  };

  const handleExcel = async () => {
    const { headers, rows } = buildData();
    if (rows.length === 0) {
      onNotify?.('Nothing to export — the protein list is empty.', 'error');
      return;
    }

    // Load the (heavy ~870KB) xlsx library on demand so it stays out of the
    // initial bundle and only downloads when the user actually exports Excel.
    const xlsxMod = await import('xlsx');
    const XLSX = xlsxMod.default || xlsxMod;

    // Build worksheet data as array-of-arrays
    const wsData = [headers, ...rows];

    // Create a worksheet with explicit string types for all cells to prevent
    // Excel from auto-converting gene names like SEPT2 / MARCH1 into dates.
    const ws = {};
    wsData.forEach((rowArr, R) => {
      rowArr.forEach((cellVal, C) => {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
        if (R === 0) {
          // Header row – plain string
          ws[cellRef] = { t: 's', v: String(cellVal) };
        } else if (typeof cellVal === 'number') {
          // Numeric values stay numeric
          ws[cellRef] = { t: 'n', v: cellVal };
        } else {
          // All text cells: force type 's' so Excel treats them as strings,
          // preventing SEPT2 → Sep-2, MARCH1 → Mar-1, etc.
          ws[cellRef] = { t: 's', v: String(cellVal) };
        }
      });
    });

    const range = { s: { r: 0, c: 0 }, e: { r: wsData.length - 1, c: (wsData[0]?.length || 1) - 1 } };
    ws['!ref'] = XLSX.utils.encode_range(range);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Proteins');
    XLSX.writeFile(wb, 'proteins_export.xlsx');

    const noun = rows.length === 1 ? 'protein' : 'proteins';
    onNotify?.(
      selectedOnly
        ? `Exported ${rows.length} selected ${noun} as proteins_export.xlsx.`
        : `Exported all ${rows.length} ${noun} currently shown as proteins_export.xlsx.`
    );
    onClose?.();
  };

  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const checkedCount = checkedFields.size;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Export to file"
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.55)'
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: 'var(--modal-bg, #1e1e1e)',
          color: 'var(--modal-fg, #eee)',
          border: '1px solid var(--modal-border, #444)',
          borderRadius: 10,
          padding: '24px 28px',
          minWidth: 340,
          maxWidth: 540,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Export to file</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'inherit', lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        <div style={{ fontSize: '0.85rem', opacity: 0.75 }}>
          {selectedOnly
            ? 'Exporting only the selected proteins.'
            : 'No rows selected — exporting all proteins currently shown in the list.'}
        </div>

        {/* Column selector */}
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>Select columns</span>
            <button onClick={selectAll} className="pill-btn" style={{ padding: '1px 8px', fontSize: '0.78rem' }}>All</button>
            <button onClick={deselectAll} className="pill-btn" style={{ padding: '1px 8px', fontSize: '0.78rem' }}>None</button>
          </div>
          <div
            style={{
              overflowY: 'auto',
              maxHeight: 260,
              display: 'flex',
              flexDirection: 'column',
              gap: 5,
              paddingRight: 4
            }}
          >
            {exportableCols.map(col => (
              <label
                key={col.field}
                style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.88rem', userSelect: 'none' }}
              >
                <input
                  type="checkbox"
                  checked={checkedFields.has(col.field)}
                  onChange={() => toggleCol(col.field)}
                />
                {col.headerName || col.field}
              </label>
            ))}
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button
            className="pill-btn"
            onClick={handleTSV}
            disabled={checkedCount === 0}
            title="Download as tab-separated values"
          >
            <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: 16, verticalAlign: 'middle' }}>
              download
            </span>{' '}
            Download TSV
          </button>
          <button
            className="pill-btn"
            onClick={handleExcel}
            disabled={checkedCount === 0}
            title="Download as Excel (.xlsx)"
          >
            <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: 16, verticalAlign: 'middle' }}>
              download
            </span>{' '}
            Download Excel
          </button>
        </div>
      </div>
    </div>
  );
}
